import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, ExternalLink, Loader2, AlertCircle, Clock } from "lucide-react";
import { SiFacebook, SiInstagram, SiGoogle, SiX, SiTiktok, SiYoutube, SiReddit } from "react-icons/si";
import { FaLinkedinIn as SiLinkedin } from "react-icons/fa";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";

interface PlatformConfig {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
  benefits: string[];
  comingSoon?: boolean;
}

const platforms: PlatformConfig[] = [
  {
    id: "facebook",
    name: "Facebook",
    icon: SiFacebook,
    color: "text-blue-600",
    description: "Erreichen Sie Millionen von Nutzern auf der weltweit größten sozialen Plattform",
    benefits: ["Umfassende Zielgruppenanalyse", "Automatische Content-Optimierung", "Facebook & Instagram Ads Management"]
  },
  {
    id: "instagram", 
    name: "Instagram",
    icon: SiInstagram,
    color: "text-pink-600",
    description: "Visuelles Storytelling und Markenaufbau durch ansprechende Inhalte",
    benefits: ["Stories & Reels Automation", "Hashtag-Optimierung", "Influencer-Content Analyse"]
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    icon: SiLinkedin, 
    color: "text-blue-700",
    description: "Professionelles Netzwerk für B2B-Marketing und Thought Leadership",
    benefits: ["Professionelle Content-Erstellung", "Lead Generation", "Branchenspezifische Insights"]
  },
  {
    id: "googlebusiness",
    name: "Google My Business",
    icon: SiGoogle,
    color: "text-green-600", 
    description: "Lokale Sichtbarkeit und Kundeninteraktion über Google-Services",
    benefits: ["Lokale SEO-Optimierung", "Bewertungsmanagement", "Google Maps Integration"]
  },
  {
    id: "twitter",
    name: "X (Twitter)",
    icon: SiX,
    color: "text-gray-900 dark:text-white",
    description: "Echtzeit-Kommunikation und Trending Topics für maximale Reichweite",
    benefits: ["Trending Hashtags nutzen", "Schnelle Kundeninteraktion", "Virale Content-Strategien"],
    comingSoon: true
  },
  {
    id: "tiktok",
    name: "TikTok",
    icon: SiTiktok,
    color: "text-black dark:text-white",
    description: "Kreative Kurzvideos für die jüngere Zielgruppe",
    benefits: ["Virale Video-Reichweite", "Trend-basierter Content", "Junge Zielgruppen erreichen"],
    comingSoon: true
  },
  {
    id: "youtube",
    name: "YouTube",
    icon: SiYoutube,
    color: "text-red-600",
    description: "Die größte Video-Plattform für langfristige Content-Strategien",
    benefits: ["Video-SEO Optimierung", "Subscriber-Wachstum", "Monetarisierung"],
    comingSoon: true
  },
  {
    id: "reddit",
    name: "Reddit",
    icon: SiReddit,
    color: "text-orange-600",
    description: "Community-basiertes Marketing für authentische Markeninteraktion",
    benefits: ["Community Engagement", "Nischen-Zielgruppen", "Authentische Diskussionen"],
    comingSoon: true
  }
];

interface OAuthPlatformCardsProps {
  onPlatformConnect?: (platform: string) => void;
}

export function OAuthPlatformCards({ onPlatformConnect }: OAuthPlatformCardsProps) {
  const [pollingPlatforms, setPollingPlatforms] = useState<Set<string>>(new Set());
  const [pollStartTimes, setPollStartTimes] = useState<Map<string, number>>(new Map());
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const { toast } = useToast();

  // Query Late.dev connected accounts with conditional polling
  // First fetch syncs with Late.dev to get fresh data
  const { data: connections = [], refetch: refetchConnections } = useQuery({
    queryKey: ['/api/late/accounts'],
    queryFn: async ({ queryKey }) => {
      const [url] = queryKey;
      // Sync with Late.dev on first load to detect existing connections
      const syncParam = isFirstLoad ? '?sync=true' : '';
      const result = await apiRequest('GET', `${url}${syncParam}`);
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }
      return result;
    },
    refetchInterval: pollingPlatforms.size > 0 ? 30000 : false, // Poll every 30 seconds if actively polling
  });

  const isConnected = (platformId: string) => {
    return connections.some((conn: any) => conn.platform === platformId && conn.isActive);
  };

  const getConnectionStatus = (platformId: string): 'connected' | 'expired' | 'needs_action' | 'not_connected' => {
    const connection = connections.find((conn: any) => conn.platform === platformId);
    if (!connection) return 'not_connected';
    return connection.connectionStatus || 'not_connected';
  };

  // Check for new connections and stop polling when found
  useEffect(() => {
    const connectedPlatforms = new Set(
      connections
        .filter((conn: any) => conn.isActive)
        .map((conn: any) => conn.platform)
    );

    pollingPlatforms.forEach(platformId => {
      if (connectedPlatforms.has(platformId)) {
        // Platform is now connected! Stop polling
        setPollingPlatforms(prev => {
          const next = new Set(prev);
          next.delete(platformId);
          return next;
        });
        
        setPollStartTimes(prev => {
          const next = new Map(prev);
          next.delete(platformId);
          return next;
        });

        const platform = platforms.find(p => p.id === platformId);
        toast({
          title: "✅ Erfolgreich verbunden!",
          description: `Ihr ${platform?.name} Konto wurde erkannt und ist jetzt verbunden.`,
        });

        // Notify parent component
        if (onPlatformConnect) {
          onPlatformConnect(platformId);
        }
      }
    });
  }, [connections, pollingPlatforms, toast, onPlatformConnect]);

  // Stop polling after 5 minutes timeout
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const FIVE_MINUTES = 5 * 60 * 1000;

      pollStartTimes.forEach((startTime, platformId) => {
        if (now - startTime > FIVE_MINUTES) {
          // Timeout reached
          setPollingPlatforms(prev => {
            const next = new Set(prev);
            next.delete(platformId);
            return next;
          });

          setPollStartTimes(prev => {
            const next = new Map(prev);
            next.delete(platformId);
            return next;
          });

          const platform = platforms.find(p => p.id === platformId);
          toast({
            title: "Zeitüberschreitung",
            description: `Die Verbindung für ${platform?.name} wurde nicht erkannt. Bitte versuchen Sie es erneut.`,
            variant: "destructive"
          });
        }
      });
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [pollStartTimes, toast]);

  const handleConnect = async (platform: PlatformConfig, force: boolean = false) => {
    console.log(`🚀 [DEBUG] handleConnect CALLED for ${platform.name}, force=${force}`);
    
    const status = getConnectionStatus(platform.id);
    console.log(`🔍 [DEBUG] Platform ${platform.id} status:`, status);
    console.log(`🔍 [DEBUG] Connections array:`, connections);
    
    if (status === 'connected' && !force) {
      console.warn(`⚠️ [DEBUG] EARLY RETURN - already connected and not forcing`);
      return;
    }

    try {
      // Call backend to get OAuth redirect URL (server-proxy method)
      console.log(`[OAuth] Starting ${platform.name} connection via server-proxy...`);
      
      const url = force 
        ? `/api/late/connect/${platform.id}?force=true`
        : `/api/late/connect/${platform.id}`;
      
      console.log(`🌐 [DEBUG] About to POST to:`, url);
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`📡 [DEBUG] Response received:`, response.status, response.statusText);
      
      if (!response.ok) {
        // Handle HTTP 409 (already connected) gracefully
        if (response.status === 409) {
          const errorData = await response.json();
          toast({
            title: "Bereits verbunden",
            description: errorData.message || `${platform.name} ist bereits verbunden.`,
            variant: "default"
          });
          // Refresh connections to update UI
          refetchConnections();
          return;
        }
        
        // Safely parse error response (guard against non-JSON)
        let errorMessage = 'Failed to start OAuth';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // Non-JSON error response, try text
          const errorText = await response.text();
          errorMessage = errorText || `HTTP ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      // Safely parse success response (guard against empty body)
      let redirectUrl: string;
      try {
        const data = await response.json();
        // Server answers { authUrl } (see server/routes/late.ts POST /connect/:platform)
        redirectUrl = data.authUrl;
        if (!redirectUrl) {
          throw new Error('No redirect URL in response');
        }
      } catch (parseError) {
        console.error('[OAuth] Failed to parse response:', parseError);
        throw new Error('Invalid server response');
      }
      
      console.log(`[OAuth] Redirecting to Late.dev OAuth: ${redirectUrl}`);
      
      // Start polling for this platform BEFORE redirect
      // (Polling will detect the connection after OAuth callback completes)
      setPollingPlatforms(prev => new Set(prev).add(platform.id));
      setPollStartTimes(prev => new Map(prev).set(platform.id, Date.now()));
      
      // Redirect user to Late.dev OAuth page (with temporary token)
      window.location.href = redirectUrl;
      
    } catch (error) {
      console.error(`[OAuth] Failed to connect ${platform.name}:`, error);
      toast({
        title: "Verbindung fehlgeschlagen",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    }
  };

  const PlatformCard = ({ platform }: { platform: PlatformConfig }) => {
    const connected = isConnected(platform.id);
    const polling = pollingPlatforms.has(platform.id);
    const status = platform.comingSoon ? 'coming_soon' : getConnectionStatus(platform.id);
    
    const getStatusBadge = () => {
      if (platform.comingSoon) {
        return (
          <Badge variant="default" className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100">
            <Clock className="w-3 h-3 mr-1" />
            Coming Soon
          </Badge>
        );
      }
      
      if (polling && !connected) {
        return (
          <Badge variant="default" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Suche...
          </Badge>
        );
      }
      
      switch (status) {
        case 'connected':
          return (
            <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
              <CheckCircle className="w-3 h-3 mr-1" />
              Verbunden
            </Badge>
          );
        case 'expired':
          return (
            <Badge variant="default" className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100">
              <AlertCircle className="w-3 h-3 mr-1" />
              Abgelaufen
            </Badge>
          );
        case 'needs_action':
          return (
            <Badge variant="default" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
              <AlertCircle className="w-3 h-3 mr-1" />
              Aktion erforderlich
            </Badge>
          );
        default:
          return null;
      }
    };

    const getDescription = () => {
      if (platform.comingSoon) return "Bald verfügbar - Wir arbeiten daran!";
      if (polling) return "Verbindung wird hergestellt...";
      
      switch (status) {
        case 'connected':
          return "Konto erfolgreich verbunden";
        case 'expired':
          return "Token abgelaufen - bitte erneut verbinden";
        case 'needs_action':
          return "Verbindung erfordert Aktion";
        default:
          return `Verbinden Sie Ihr ${platform.name} Konto sicher`;
      }
    };
    
    return (
      <Card 
        className={`relative transition-all duration-200 hover-elevate ${status === 'connected' ? 'ring-2 ring-green-500' : ''} ${status === 'expired' ? 'ring-2 ring-orange-500' : ''}`}
        data-testid={`card-platform-${platform.id}`}
      >
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <platform.icon className={`w-8 h-8 ${platform.color}`} />
              <div>
                <CardTitle className="text-lg">{platform.name}</CardTitle>
                <CardDescription className="text-sm">
                  {getDescription()}
                </CardDescription>
              </div>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        
        <CardContent className="pt-0">
          <p className="text-sm text-muted-foreground mb-4">{platform.description}</p>
          
          <div className="space-y-2 mb-6">
            <h4 className="text-sm font-medium">Vorteile:</h4>
            <ul className="space-y-1">
              {platform.benefits.map((benefit, index) => (
                <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                  <CheckCircle className="w-3 h-3 mt-0.5 text-green-600 flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
          
          {polling && !connected && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">Verbindung wird überwacht</p>
                  <p>Sobald Sie {platform.name} verbunden haben, erkennen wir dies automatisch.</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Button
              onClick={() => handleConnect(platform)}
              disabled={polling || status === 'connected' || platform.comingSoon}
              className="w-full"
              variant={status === 'connected' ? "secondary" : platform.comingSoon ? "outline" : "default"}
              data-testid={`button-connect-${platform.id}`}
            >
              {platform.comingSoon ? (
                <>
                  <Clock className="w-4 h-4 mr-2" />
                  Bald verfügbar
                </>
              ) : polling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verbindung wird hergestellt...
                </>
              ) : status === 'connected' ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Verbunden
                </>
              ) : status === 'expired' ? (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Erneut verbinden
                </>
              ) : status === 'needs_action' ? (
                <>
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Verbindung aktualisieren
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Mit {platform.name} verbinden
                </>
              )}
            </Button>
            
            {status === 'connected' && !platform.comingSoon && (
              <Button
                onClick={() => handleConnect(platform, true)}
                disabled={polling}
                className="w-full"
                variant="outline"
                size="sm"
                data-testid={`button-refresh-${platform.id}`}
              >
                <ExternalLink className="w-3 h-3 mr-2" />
                Verbindung erneuern
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="oauth-platform-cards">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Plattform verbinden</h2>
        <p className="text-muted-foreground">
          Verbinden Sie Ihre Social Media Konten
        </p>
      </div>
      
      {pollingPlatforms.size > 0 && (
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                  Überwache Verbindungen...
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Wir prüfen alle 30 Sekunden, ob neue Accounts verbunden wurden. Dies stoppt automatisch nach 5 Minuten oder wenn die Verbindung erkannt wurde.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platforms.map((platform) => (
          <PlatformCard key={platform.id} platform={platform} />
        ))}
      </div>
      
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Ihre Daten werden sicher verschlüsselt und nur für autorisierte Marketing-Aktivitäten verwendet.
        </p>
      </div>
    </div>
  );
}
